import { Inject, Injectable, Optional } from '@angular/core';
import { LegitimationApi } from '@core/data-access/services/api/legitimation-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LEGITIMATION_DEBOUNCE_TIME } from '@shared/tokens';
import {
  AddLegitimationToList,
  GetLegitimationFaxId,
  GetLegitimationList,
  GetLegitimationStatus,
  ActionTypes as LegitimationActionTypes,
  PostLegitimationInfo,
  SetLegitimationFaxId,
  SetLegitimationInfoResponse,
  SetLegitimationStatus
} from '@store/legitimation/legitimation.actions';
import {
  LegitimationFaxInfoModel,
  LegitimationListModel,
  LegitimationStatus
} from '@store/legitimation/legitimation.interface';
import { catchError, debounceTime, EMPTY, map, of, switchMap } from 'rxjs';

@Injectable()
export class LegitimationEffect {
  loadLegitimationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLegitimationList>(LegitimationActionTypes.GET_LEGITIMATION_LIST),
      debounceTime(this.legitimationDebounceTime),
      switchMap(() => {
        return this.legitimationApi.getLegitimationList$().pipe(
          map((legitimationList: LegitimationListModel[]) => new AddLegitimationToList(legitimationList)),
          catchError(() => of(new AddLegitimationToList([])))
        );
      })
    )
  );

  loadLegitimationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLegitimationStatus>(LegitimationActionTypes.GET_LEGITIMATION_STATUS),
      debounceTime(this.legitimationDebounceTime),
      switchMap((data: any) => {
        const { userId, eventId } = data.payload;
        if (eventId === '' || userId === '') {
          return EMPTY;
        }

        return this.legitimationApi.getLegitimationStatus$(eventId).pipe(
          map(({ status }) => new SetLegitimationStatus(status)),
          catchError(() => of(new SetLegitimationStatus(LegitimationStatus.OtherStatus)))
        );
      })
    )
  );

  loadLegitimationFaxId$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLegitimationFaxId>(LegitimationActionTypes.GET_LEGITIMATION_FAX_ID),
      debounceTime(this.legitimationDebounceTime),
      switchMap((data: GetLegitimationFaxId) => {
        const { eventId, userId } = data.payload;
        if (eventId === null) {
          return EMPTY;
        }

        return this.legitimationApi.getLegitimationFaxId$(eventId, userId).pipe(
          map((legitimationFaxInfo: LegitimationFaxInfoModel) => new SetLegitimationFaxId(legitimationFaxInfo.faxId)),
          catchError(() => of(new SetLegitimationFaxId(null)))
        );
      })
    )
  );

  postLegitimationInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PostLegitimationInfo>(LegitimationActionTypes.POST_LEGITIMATION_INFO),
      debounceTime(this.legitimationDebounceTime),
      switchMap((data: PostLegitimationInfo) => {
        if (!data.payload) {
          return EMPTY;
        }

        const { feed, userId, eventId } = data.payload;

        return this.legitimationApi.postSaveLegitimationInfo$(feed, userId, eventId).pipe(
          map(() => new SetLegitimationInfoResponse(true)),
          catchError(() => of(new SetLegitimationInfoResponse(false)))
        );
      })
    )
  );

  constructor(
    @Optional() @Inject(LEGITIMATION_DEBOUNCE_TIME) private legitimationDebounceTime: number,
    private actions$: Actions,
    private legitimationApi: LegitimationApi
  ) {}
}
